import moment from 'moment'
import { RefObject } from 'react'
import { TimeComponent } from 'types'

export function scrollToEnd(ref: RefObject<HTMLInputElement>) {
  if (ref.current) ref.current.scrollLeft = ref.current.scrollWidth
}

function pad(value: number, padNum: number) {
  return value.toString().padStart(padNum, '0')
}

/**
 * dateOfBirth should be a UTC midnight date in ISO datetimeFormat
 * EX. 1988-05-17T00:00:00.000Z
 */
export const getDateComponents = (
  date?: string
): { __day?: string; __month?: string; __year?: string } => {
  const initialDate = moment(date).utc().format('YYYY-MM-DD')
  if (date && initialDate) {
    return {
      __day: pad(moment(initialDate).date(), 2),
      __month: pad(moment(initialDate).month() + 1, 2),
      __year: moment(initialDate).year().toString(),
    }
  }

  return {}
}

export const withoutUndefined = (obj?: { [key: string]: any }) => {
  if (!obj) return obj
  else {
    const cleanObj = { ...obj }
    Object.keys(cleanObj).forEach((key) => {
      if (obj[key] === undefined) {
        delete cleanObj[key]
      }
    })
    return cleanObj
  }
}

export const withoutFalsy = (obj: Object) => {
  return Object.entries(obj).reduce((acc, [key, val]) => {
    if (val) {
      acc[key] = val
      return acc
    } else {
      return acc
    }
  }, {} as any)
}

export function getChangedValues<FormValues extends {}>({
  initialValues,
  values,
}: {
  initialValues: FormValues
  values: FormValues
}): Partial<FormValues> {
  const currValues = { ...values }
  //we never send these with the update button
  //match the current to init values
  const fieldNameArray = (Object.keys(
    currValues
  ) as unknown) as (keyof FormValues)[]
  const changedValues = fieldNameArray.reduce((changedValues, fieldName) => {
    if (
      currValues[fieldName] !== initialValues[fieldName] &&
      JSON.stringify(currValues[fieldName]) !==
        JSON.stringify(initialValues[fieldName])
    ) {
      changedValues[fieldName] = currValues[fieldName]
    }
    return changedValues
  }, {} as Partial<FormValues>)
  return changedValues
}

export function hasChanged<FormValues extends {}>({
  initialValues,
  values,
}: {
  initialValues: FormValues
  values: FormValues
}): boolean {
  const changed = getChangedValues<FormValues>({ initialValues, values })
  return !!Object.keys(changed).length
}

export const MIN_IN_MS = 1000 * 60 // 1000 ms/sec * 60 sec/min
export const HOUR_IN_MS = MIN_IN_MS * 60 // MIN_IN_MS ms/min * 60 mins/hour
export const DAY_IN_MS = HOUR_IN_MS * 24 // HOUR_IN_MS ms/hour * 24 hours/day
//HH:MM 24 hour format => ms
export const msFromTimeString = (time?: string | null) => {
  if (time === undefined || time === null) return undefined
  const [hours, mins] = time.split(':').map((numString) => Number(numString))
  if (isNaN(hours) || hours > 23) return undefined
  if (isNaN(mins) || mins > 59) return undefined
  return hours * HOUR_IN_MS + mins * MIN_IN_MS
}

//ms => HH:MM 24 hour format
export const timeStringFromMS = (time?: number) => {
  if (time === undefined || time >= DAY_IN_MS) return undefined
  let hours = 0
  let mins = 0
  while (time >= HOUR_IN_MS) {
    time -= HOUR_IN_MS
    hours++
  }
  while (time >= MIN_IN_MS) {
    time -= MIN_IN_MS
    mins++
  }
  return `${pad(hours, 2)}:${pad(mins, 2)}`
}

//ms => HH:MM AM/PM
export const formatTime = (msAfterMidnight: number) => {
  const time = new Date(msAfterMidnight)
  const formatted = time.toLocaleString('en-US', {
    timeZone: 'UTC',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })
  return formatted
}

const INVALID_TIME_RETURN_VALUE = {
  hours: undefined,
  minutes: undefined,
  partOfDay: undefined,
}

/** 14:32 => {hours: 2, minutes: 32, partOfDay: 'pm'} */
export const componentsFromTimeString = (
  timeString?: string | null
): TimeComponent => {
  if (!timeString) return INVALID_TIME_RETURN_VALUE
  else {
    const [hours24, minutes] = timeString
      .split(':')
      .map((component) => Number(component))
    if (isNaN(hours24) || isNaN(minutes)) return INVALID_TIME_RETURN_VALUE
    else {
      const hours = hours24 === 0 ? 12 : hours24 <= 12 ? hours24 : hours24 - 12
      const partOfDay = hours24 < 12 ? 'am' : 'pm'
      return {
        hours,
        minutes,
        partOfDay,
      }
    }
  }
}

/** {hours: 2, minutes: 32, partOfDay: 'pm'} => 14:32  */
export const timeStringFromComponent = ({
  hours: hours12,
  minutes,
  partOfDay,
}: TimeComponent): string | undefined => {
  if (
    hours12 !== undefined &&
    minutes !== undefined &&
    partOfDay !== undefined
  ) {
    const hours =
      hours12 === 12 && partOfDay === 'am'
        ? '00'
        : hours12 === 12 && partOfDay === 'pm'
        ? '12'
        : partOfDay === 'pm'
        ? hours12 + 12
        : hours12
    const formattedValue = timeStringFromMS(
      msFromTimeString(`${hours}:${minutes}`)
    )
    return formattedValue
  } else {
    return undefined
  }
}

export const preFillForm = <FormValues>(
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void,
  data: Partial<FormValues>
) => {
  Object.entries(data).forEach(([key, val]) => {
    if (val) setFieldValue(key, val)
  })
}

export const roundToNearest = (
  ms?: number,
  friendlyNumber: number = MIN_IN_MS
): number | undefined => {
  if (ms === undefined || ms === null) return undefined
  const remainder = ms % friendlyNumber // get how much it goes past the closest friendlyNumber
  if (remainder > friendlyNumber / 2) {
    return ms + (friendlyNumber - remainder)
  } else {
    return ms - remainder
  }
}
export const removeDuplicates = (arr: string[]): string[] => {
  const alreadyAdded = new Set<string>()
  return arr.filter((id) => {
    if (!alreadyAdded.has(id)) {
      alreadyAdded.add(id)
      return true
    } else {
      return false
    }
  })
}
